import { CATEGORIES_DOSSIER, type CategorieDossier } from '@repere/core';
import { useRegime } from '../abonnement/regime';
import { Invisible, Picto } from '../ui';
import { ECRAN_LARGE, useRequeteMedia } from '../ui/media';
import { useTraduction } from '../i18n/langue';

/**
 * Les catégories que la barre du bas ne porte pas, et que le menu reprend.
 *
 * Six onglets sur 360 px, c'est 60 px par cible : le libellé y tombait sous la taille
 * lisible et les pictogrammes se touchaient. Les enfants sont la seule qu'on ne consulte
 * pas tous les jours — on y va pour corriger une fiche, pas pour savoir chez qui dort
 * l'enfant ce soir — et c'est donc elle qui cède la place. Le journal est revenu dans la
 * barre : cinq onglets serrés restent plus proches d'un geste quotidien qu'un tiroir à
 * ouvrir pour consigner un fait du jour. Les cinq qui restent se partagent la largeur.
 *
 * **Le rail de l'écran large garde les six** : il a la hauteur pour, et rien n'y est à
 * l'étroit. Cette liste ne décrit donc pas une hiérarchie du produit, seulement ce qui
 * tient dans une rangée de 360 px.
 */
export const CATEGORIES_EN_MENU: readonly CategorieDossier[] = ['enfants'];

/**
 * La fonction d'écriture dont chaque catégorie dépend.
 *
 * « Enfants » n'y figure pas : la fiche d'un enfant s'écrit sans abonnement, seuls les
 * documents qu'on y dépose le demandent. Marquer l'onglet entier annoncerait un mur qui
 * n'existe pas, et un onglet marqué à tort est pire qu'un onglet non marqué — le parent
 * renonce à y aller.
 */
const FONCTION_DE_LA_CATEGORIE: Partial<Record<CategorieDossier, string>> = {
  messages: 'messagerie',
  depenses: 'depenses',
  journal: 'journal',
};

/**
 * Navigation entre les catégories d'un dossier.
 *
 * Un seul composant pour les deux dispositions : la même liste de boutons, replacée par
 * la feuille de style — rail à gauche sur grand écran, barre en bas sur téléphone. Les
 * dupliquer en deux composants ferait diverger l'ordre, les libellés et les pastilles à
 * la première retouche, et le parent qui passe du téléphone à l'ordinateur ne
 * retrouverait plus ses repères.
 *
 * `<nav>` et `aria-current="page"` plutôt qu'un état purement visuel : au lecteur d'écran,
 * « Messages, page actuelle » est la seule façon de savoir où l'on est.
 *
 * La pastille annonce un **compte de faits survenus**, jamais une injonction : « 2 » à
 * côté de Messages dit qu'il y a deux messages de plus, pas que vous devez répondre.
 *
 * L'urgence n'est plus ici. Elle occupait une barre rouge pleine largeur sous la barre des
 * catégories : sur téléphone, deux rangées de commandes empilées mangeaient le bas de tous
 * les écrans du dossier. Elle a rejoint le menu de l'en-tête, en première entrée — un
 * geste n'est pas une catégorie, et le menu est la seule porte qui reste à portée du pouce
 * sans occuper de place.
 */
export function Navigation({
  categorieActive,
  nouveautes,
  surChoix,
}: {
  categorieActive: CategorieDossier;
  /** Nombre de faits nouveaux par catégorie. Une catégorie absente n'a rien de nouveau. */
  nouveautes: Record<string, number>;
  surChoix: (categorie: CategorieDossier) => void;
}) {
  const { messages } = useTraduction();
  // Les droits sont partagés avec le badge de l'en-tête : même clé, donc une requête.
  const droits = useRegime();

  // Le rail porte les six catégories, la barre du bas les quatre du quotidien : voir
  // `CATEGORIES_EN_MENU`. La bascule est ici et non dans la feuille de style parce qu'une
  // catégorie cachée en CSS resterait dans l'ordre de tabulation et dans le plan annoncé
  // au lecteur d'écran — elle serait absente de l'écran, mais pas du parcours.
  const rail = useRequeteMedia(ECRAN_LARGE);
  const categories = rail
    ? CATEGORIES_DOSSIER
    : CATEGORIES_DOSSIER.filter((categorie) => !CATEGORIES_EN_MENU.includes(categorie));

  /**
   * Une catégorie est marquée si son écriture demande l'abonnement.
   *
   * Tant que les droits ne sont pas connus, **rien n'est marqué** : annoncer un abonnement
   * à quelqu'un qui l'a déjà, le temps d'une requête, serait la pire des sollicitations.
   */
  const demandeAbonnement = (categorie: CategorieDossier): boolean => {
    const fonction = FONCTION_DE_LA_CATEGORIE[categorie];
    const ouvertes = droits.data?.ecriture;
    // `Array.isArray` et non une simple présence : une réponse inattendue ne doit pas
    // faire tomber la navigation entière du dossier pour une pastille décorative.
    if (!fonction || !Array.isArray(ouvertes)) return false;
    return !ouvertes.includes(fonction);
  };

  return (
    <nav className="dossier-navigation" aria-label={messages.dossier.navigation.ariaCategories}>
      <ul>
        {categories.map((categorie) => {
          const compte = nouveautes[categorie] ?? 0;
          const actif = categorie === categorieActive;
          const reserve = demandeAbonnement(categorie);
          return (
            <li key={categorie}>
              <button
                type="button"
                className={actif ? 'onglet actif' : 'onglet'}
                aria-current={actif ? 'page' : undefined}
                onClick={() => surChoix(categorie)}
              >
                {/* Ce qui demande Solo ou Duo se dit par la **couleur du pictogramme**, en
                    or, et non plus par une couronne posée à côté du libellé.

                    La couronne était un second objet dans un onglet qui en portait déjà
                    trois — pictogramme, libellé, pastille de nouveauté. Sur la barre du
                    téléphone, où le libellé passe sous le dessin à 12 px, elle se
                    retrouvait collée au mot et se lisait comme une pastille de plus. Le
                    dessin lui-même n'a pas besoin d'un compagnon pour changer de sens :
                    il change de teinte, et il n'y a plus qu'un objet à lire.

                    La couleur ne dit **jamais** seule : `title` porte la phrase complète
                    au survol, et le suffixe invisible la porte au lecteur d'écran
                    (WCAG 1.4.1). C'est ce que la couronne faisait déjà, à l'identique. */}
                <span
                  className={reserve ? 'onglet-picto reserve' : 'onglet-picto'}
                  {...(reserve ? { title: messages.dossier.navigation.reserveParent } : {})}
                >
                  <Picto categorie={categorie} />
                  {/* Sur téléphone, `.onglet-compte` cède la place à ce badge posé sur
                      l'icône — un chiffre tient sous un onglet trop étroit pour son
                      libellé complet, un point ne le disait pas. */}
                  {compte > 0 ? (
                    <span className="onglet-point" aria-hidden="true">
                      {compte > 9 ? '9+' : compte}
                    </span>
                  ) : null}
                </span>
                <span className="onglet-libelle">{messages.dossier.categories[categorie]}</span>
                {reserve ? (
                  <Invisible>{messages.dossier.navigation.reserveParentSuffixe}</Invisible>
                ) : null}
                {compte > 0 ? (
                  <>
                    <span className="onglet-compte" aria-hidden="true">
                      {compte > 99 ? '99+' : compte}
                    </span>
                    <Invisible>{messages.dossier.navigation.nouveaute(compte)}</Invisible>
                  </>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
